import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { getConsent, saveConsent } from "@/lib/consent";

const btn = "min-h-10 px-3 py-2 leading-tight font-display font-bold uppercase tracking-widest text-xs transition-colors";

const Row = ({ title, desc, children }) => (
  <div className="flex items-start justify-between gap-4 border-t border-[#3B4157] pt-3">
    <div><div className="text-sm font-semibold text-slate-200">{title}</div><div className="text-xs text-slate-500 mt-0.5">{desc}</div></div>
    {children}
  </div>
);

export function CookieBanner() {
  const { t } = useLang();
  const [open, setOpen] = useState(() => !getConsent());
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(() => !!getConsent()?.analytics);

  useEffect(() => {
    const on = () => { setAnalytics(!!getConsent()?.analytics); setSettings(true); setOpen(true); };
    window.addEventListener("cookie-settings", on);
    return () => window.removeEventListener("cookie-settings", on);
  }, []);

  if (!open) return null;
  const done = (a) => { saveConsent(a); setOpen(false); setSettings(false); toast.success(t("cookie_saved")); };

  return (
    <div data-testid="cookie-banner" className="fixed z-[60] bottom-4 left-4 right-4 sm:right-auto sm:max-w-md border border-[#3B4157] bg-[#292D3C]/95 backdrop-blur-xl p-5 shadow-2xl shadow-black/60 animate-in fade-in slide-in-from-bottom-4">
      <div className="flex items-center gap-2 font-display font-bold uppercase tracking-widest text-sm"><Cookie className="w-4 h-4 text-orange-500" />{t("cookie_title")}</div>
      <p className="text-sm text-slate-400 mt-2">{t("cookie_text")} <Link to="/cookies" data-testid="cookie-policy-link" className="text-orange-500 underline underline-offset-2">{t("cookies_policy")}</Link></p>
      {settings && (
        <div data-testid="cookie-settings-panel" className="mt-4 space-y-3">
          <Row title={t("cookie_nec_title")} desc={t("cookie_nec_desc")}><span className="text-[10px] font-mono-x uppercase text-slate-500 pt-1">{t("always_on")}</span></Row>
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button data-testid="cookie-accept-all-btn" onClick={() => done(true)} className={`${btn} bg-orange-500 text-[#1F2230] hover:bg-orange-400`}>{t("cookie_accept_all")}</button>
        <button data-testid="cookie-necessary-btn" onClick={() => done(false)} className={`${btn} border border-[#3B4157] text-slate-200 hover:border-orange-500`}>{t("cookie_necessary_only")}</button>
        {settings
          ? <button data-testid="cookie-save-btn" onClick={() => done(analytics)} className={`${btn} col-span-2 text-orange-500 hover:text-orange-400`}>{t("cookie_save")}</button>
          : <button data-testid="cookie-settings-btn" onClick={() => setSettings(true)} className={`${btn} col-span-2 text-slate-400 hover:text-white`}>{t("cookie_settings")}</button>}
      </div>
    </div>
  );
}
